import cn from 'classnames';
import { ReactNode, useEffect, useRef } from 'react';

import { useId } from 'hooks/useId';

import styles from './styles.module.scss';

export function Popover({
  visible,
  classes,
  children,
  popoverChildren,
}: {
  visible: boolean;

  children: ReactNode;
  popoverChildren: ReactNode;
  classes?: string;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const anchorId = useId();

  useEffect(() => {
    const currentRefVal = popoverRef.current;

    if (!currentRefVal) return;

    if (visible) currentRefVal.showPopover();
    return () => void currentRefVal.hidePopover();
  }, [visible]);

  // this whole effect is only needed _iff_ the browser doesn't support anchor positioning; monitor https://developer.mozilla.org/en-US/docs/Web/CSS/anchor-name for baseline compatibility
  const anchorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!visible) return; // don't need this if the popover isn't visible
    if (CSS.supports('anchor-name', '--test-anchor')) return; // we don't need this fix if anchor positioning works

    const popover = popoverRef.current!;
    const anchor = anchorRef.current!;

    const listener = () => {
      const anchorRect = anchor.getBoundingClientRect();

      popover.style.top = `${anchorRect.bottom}px`;
      popover.style.left = `${anchorRect.left}px`;
      popover.style.width = `${anchorRect.right - anchorRect.left}px`;
      popover.style.maxHeight = `${window.innerHeight - anchorRect.bottom}px`;
    };

    listener();

    const controller = new AbortController();

    window.addEventListener('resize', listener, { signal: controller.signal });

    for (
      let element = anchor.parentElement;
      element !== document.body && element !== null;
      element = element.parentElement
    ) {
      if (
        element.clientHeight !== element.scrollHeight ||
        element.clientWidth !== element.scrollWidth
      ) {
        element.addEventListener('scroll', listener, {
          signal: controller.signal,
        });
      }
    }

    return () => {
      controller.abort();
    };
  }, [visible]);

  return (
    <>
      <div ref={anchorRef} style={{ anchorName: `--${anchorId}` }}>
        {children}
        <div
          ref={popoverRef}
          // @ts-expect-error types are missing this attribute https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/popover
          popover="manual"
          className={cn(styles.popover, classes)}
          style={{ positionAnchor: `--${anchorId}` }}
        >
          {popoverChildren}
        </div>
      </div>
    </>
  );
}
